import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ChatCardItemState, PromptCoverage } from '@karehero/models';

import { useCheckPromptCoverageMutation } from 'store/api/inference';

import { useLiveCheck, wordsOf } from './useLiveCheck';

/** Words unchanged this long are a pause, which ticks the prompt they were on. */
export const PAUSE_MS = 4000;

const STATE_OF: Record<PromptCoverage, ChatCardItemState> = {
  not_started: 'waiting',
  started: 'doing',
  covered: 'done',
};

const RANK: Record<ChatCardItemState, number> = {
  waiting: 0,
  doing: 1,
  done: 2,
};

/** Each prompt's state, by prompt. */
export type PromptStates = Readonly<Record<string, ChatCardItemState>>;

const waiting = (prompts: readonly string[]): PromptStates =>
  Object.fromEntries(prompts.map((prompt) => [prompt, 'waiting']));

/** Each prompt's state from the coverage found, which answers in the prompts' order; waiting where it says nothing. */
const statesOf = (
  coverage: readonly PromptCoverage[],
  prompts: readonly string[],
): PromptStates =>
  Object.fromEntries(
    prompts.map((prompt, i) => [prompt, STATE_OF[coverage[i]] ?? 'waiting']),
  );

/** Each prompt's state as found, and the prompt they may still be adding to. */
export interface Coverage {
  states: PromptStates;
  stillOn?: string;
}

/** Each prompt at the furthest it has reached, so a tick never goes back while words are added. */
export const furthest = (
  shown: PromptStates,
  found: PromptStates,
): PromptStates =>
  Object.fromEntries(
    Object.entries(shown).map(([prompt, state]) => {
      const next = found[prompt] ?? state;
      return [prompt, RANK[next] > RANK[state] ? next : state];
    }),
  );

/** The states found so far, moved on by the newest check, which also says what they are on now. */
const furthestCoverage = (shown: Coverage, found: Coverage): Coverage => ({
  states: furthest(shown.states, found.states),
  stillOn: found.stillOn,
});

/** Each prompt as shown: one with enough said stays doing while they are still on it, until they pause or it has been ticked. */
export const heldStates = (
  { states, stillOn }: Coverage,
  { isPaused, ticked }: { isPaused: boolean; ticked: readonly string[] },
): PromptStates =>
  Object.fromEntries(
    Object.entries(states).map(([prompt, state]) => [
      prompt,
      state === 'done' &&
      prompt === stillOn &&
      !isPaused &&
      !ticked.includes(prompt)
        ? 'doing'
        : state,
    ]),
  );

/** Whether the words have stayed as they are for the length of a pause. */
const useIsPaused = (words: string) => {
  const [pausedOn, setPausedOn] = useState<string>();
  useEffect(() => {
    const timer = setTimeout(() => setPausedOn(words), PAUSE_MS);
    return () => clearTimeout(timer);
  }, [words]);
  return pausedOn === words;
};

export interface UsePromptCoverageOptions {
  careCircleId?: string;
  prompts: readonly string[];
  /** Everything said so far, typed or spoken, live words included. */
  said: string;
  isEnabled: boolean;
}

/**
 * usePromptCoverage ticks off prompts as they are spoken to: each is waiting,
 * doing once begun, and done once enough is said. One they are still adding
 * to waits as doing until they move on or pause, so a tick does not cut them
 * short; once ticked it stays. A tick goes back if the words that earned it
 * are changed or taken back before they are sent.
 */
export const usePromptCoverage = ({
  careCircleId,
  prompts,
  said,
  isEnabled,
}: UsePromptCoverageOptions): {
  states: PromptStates;
  /** Whether what is said has been checked, so the ticks shown are all there are for now. */
  isCaughtUp: boolean;
} => {
  const [checkCoverage] = useCheckPromptCoverageMutation();
  const initial = useMemo(() => ({ states: waiting(prompts) }), [prompts]);
  const check = useCallback(
    async (text: string): Promise<Coverage> => {
      const { coverage, stillOn } = await checkCoverage({
        careCircleId: careCircleId ?? '',
        body: { prompts: [...prompts], said: text },
      }).unwrap();
      return {
        states: statesOf(coverage, prompts),
        stillOn: stillOn === undefined ? undefined : prompts[stillOn],
      };
    },
    [careCircleId, checkCoverage, prompts],
  );
  const { value: found, isCaughtUp } = useLiveCheck({
    text: said,
    isEnabled: isEnabled && !!careCircleId,
    initial,
    check,
    keepFurthest: furthestCoverage,
  });

  const isPaused = useIsPaused(wordsOf(said));
  const [ticked, setTicked] = useState<readonly string[]>([]);
  const shown = useMemo(
    () => heldStates(found, { isPaused, ticked }),
    [found, isPaused, ticked],
  );
  // Once shown done, a prompt stays ticked while they come back to it.
  const shownDone = prompts.filter((prompt) => shown[prompt] === 'done');
  const shownDoneKey = shownDone.join('\n');
  useEffect(() => {
    setTicked(shownDoneKey ? shownDoneKey.split('\n') : []);
  }, [shownDoneKey]);

  return { states: shown, isCaughtUp };
};
